import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Slider,
  Tooltip,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ColorLensRoundedIcon from '@mui/icons-material/ColorLensRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import AspectRatioRoundedIcon from '@mui/icons-material/AspectRatioRounded';
import GrainRoundedIcon from '@mui/icons-material/GrainRounded';
import CompareRoundedIcon from '@mui/icons-material/CompareRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import { useTheme } from '../../../ThemeContext';
import { GooglePlayIcon } from '../../common/StoreIcons';
import { EDITOR_PLAY_STORE_URL } from '../../../constants/links';
import FilmhookColoristScopeHUD from './FilmhookColoristScopeHUD';

// High-resolution cinema sample stills from assets
import sceneNight from '../../../assets/ChatGPT Image Aug 24, 2026, 01_52_21 PM.png';
import scenePortrait from '../../../assets/ChatGPT Image Aug 24, 2026, 01_54_39 PM.png';
import sceneStudio from '../../../assets/ChatGPT Image Aug 24, 2026, 02_00_10 PM.png';

// 3D LUT Presets
export const LUT_PRESETS = [
  {
    id: 'kodak-500t',
    name: 'Kodak Vision3 500T',
    subtitle: 'Vintage 35mm Motion Picture Film Grain',
    icon: '🎞️',
    color: '#f59e0b',
    swatch: 'linear-gradient(135deg, #d97706 0%, #b45309 50%, #78350f 100%)',
    description: 'Warm organic skin tones, halation in highlights, deep analog shadow roll-off.',
    filter: 'contrast(1.2) sepia(0.24) saturate(1.15) brightness(0.96) hue-rotate(-7deg)',
    overlay: 'linear-gradient(180deg, rgba(245, 158, 11, 0.12) 0%, rgba(180, 83, 9, 0.18) 100%)',
    blendMode: 'soft-light',
    specs: { temp: '3200K Tungsten', grain: '16mm Analog', contrast: '+18%', saturation: '+15%' },
  },
  {
    id: 'teal-orange',
    name: 'Blockbuster Teal & Orange',
    subtitle: 'Hollywood High-Contrast Color Separation',
    icon: '🌊',
    color: '#06b6d4',
    swatch: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 50%, #f97316 100%)',
    description: 'Complementary color science pushing cold cyan shadows against radiant warm amber faces.',
    filter: 'contrast(1.32) saturate(1.4) brightness(0.98) hue-rotate(6deg)',
    overlay: 'linear-gradient(135deg, rgba(6, 182, 212, 0.28) 0%, rgba(249, 115, 22, 0.25) 100%)',
    blendMode: 'color',
    specs: { temp: '5600K Split', grain: 'Fine Digital 4K', contrast: '+32%', saturation: '+40%' },
  },
  {
    id: 'bleach-bypass',
    name: 'Bleach Bypass (Silver Retain)',
    subtitle: 'Gritty Thriller & Desaturated Metallic Shadows',
    icon: '🖤',
    color: '#94a3b8',
    swatch: 'linear-gradient(135deg, #cbd5e1 0%, #475569 50%, #0f172a 100%)',
    description: 'Photochemical silver retention style. High contrast, desaturated tones and brutalist textures.',
    filter: 'contrast(1.48) saturate(0.42) brightness(0.92) grayscale(0.25)',
    overlay: 'linear-gradient(180deg, rgba(148, 163, 184, 0.18) 0%, rgba(15, 23, 42, 0.45) 100%)',
    blendMode: 'overlay',
    specs: { temp: '4800K Cool', grain: 'Heavy Silver Hash', contrast: '+48%', saturation: '-58%' },
  },
  {
    id: 'cyberpunk-neon',
    name: 'Cyberpunk Neon Glow',
    subtitle: 'Neo-Tokyo Ultraviolet & Electric Cyan Bloom',
    icon: '🌆',
    color: '#ec4899',
    swatch: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #06b6d4 100%)',
    description: 'Hyper-vivid electric aesthetic with saturated magentas, luminescent violets, and glowing blooms.',
    filter: 'contrast(1.38) saturate(1.68) brightness(1.04) hue-rotate(-24deg)',
    overlay: 'linear-gradient(135deg, rgba(236, 72, 153, 0.25) 0%, rgba(139, 92, 246, 0.25) 50%, rgba(6, 182, 212, 0.25) 100%)',
    blendMode: 'screen',
    specs: { temp: 'Neon Tri-Tone', grain: 'Digital Noise', contrast: '+38%', saturation: '+68%' },
  },
  {
    id: 'cinema-noir',
    name: 'Master Monochrome Noir',
    subtitle: 'Dramatic 1950s High-Dynamic Contrast Film',
    icon: '📽️',
    color: '#e2e8f0',
    swatch: 'linear-gradient(135deg, #ffffff 0%, #64748b 50%, #000000 100%)',
    description: 'Pure black-and-white tonal curve modeled after Kodak Tri-X 400 with deep velvet shadows.',
    filter: 'grayscale(1) contrast(1.55) brightness(0.95)',
    overlay: 'radial-gradient(circle, transparent 40%, rgba(0,0,0,0.5) 100%)',
    blendMode: 'multiply',
    specs: { temp: 'Monochrome', grain: 'Tri-X 400 Grain', contrast: '+55%', saturation: '0%' },
  },
];

const SCENE_OPTIONS = [
  { id: 'scene-1', title: 'Night Street & Rain', subtitle: 'Reflections & Lights', image: sceneNight },
  { id: 'scene-2', title: 'Dramatic Close-Up', subtitle: 'Skin Tones & Key Light', image: scenePortrait },
  { id: 'scene-3', title: 'Director Studio Dailies', subtitle: '4K Studio Footage', image: sceneStudio },
];

/**
 * Interactive Split-Screen 3D LUT Color Grading Sandbox Modal for Filmhook Editor
 */
export const FilmhookColorLUTSandboxModal = ({ open, onClose }) => {
  const { isDarkMode } = useTheme();
  const [selectedLutId, setSelectedLutId] = useState('teal-orange');
  const [selectedSceneId, setSelectedSceneId] = useState('scene-1');
  const [sliderPos, setSliderPos] = useState(50);
  const [intensity, setIntensity] = useState(100);
  const [enableGrain, setEnableGrain] = useState(true);
  const [enableCinemascope, setEnableCinemascope] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isCopiedLut, setIsCopiedLut] = useState(false);
  const [isAutoScanning, setIsAutoScanning] = useState(false);
  const [showScopes, setShowScopes] = useState(true);

  const containerRef = useRef(null);
  const scanDirRef = useRef(1);
  const animScanRef = useRef(null);
  const lastScanTimeRef = useRef(null);

  const activeLut = LUT_PRESETS.find((l) => l.id === selectedLutId) || LUT_PRESETS[0];
  const activeScene = SCENE_OPTIONS.find((s) => s.id === selectedSceneId) || SCENE_OPTIONS[0];

  // Dragging event handlers for slider
  const handleMove = (clientX) => {
    setIsAutoScanning(false);
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.min(Math.max((x / rect.width) * 100, 5), 95);
    setSliderPos(percent);
  };

  const handleTouchMove = (e) => {
    if (e.touches && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };

  // Automated Cinema Wipe Scanner Animation (smooth ping-pong wipe between 12% and 88%)
  useEffect(() => {
    if (!isAutoScanning) {
      if (animScanRef.current) cancelAnimationFrame(animScanRef.current);
      lastScanTimeRef.current = null;
      return;
    }

    const sweepSpeed = 24; // percent per second
    const minSweep = 12;
    const maxSweep = 88;

    const animateSweep = (timestamp) => {
      if (!lastScanTimeRef.current) lastScanTimeRef.current = timestamp;
      const deltaSec = (timestamp - lastScanTimeRef.current) / 1000;
      lastScanTimeRef.current = timestamp;

      setSliderPos((prev) => {
        let next = prev + scanDirRef.current * sweepSpeed * deltaSec;
        if (next >= maxSweep) {
          next = maxSweep;
          scanDirRef.current = -1;
        } else if (next <= minSweep) {
          next = minSweep;
          scanDirRef.current = 1;
        }
        return next;
      });

      animScanRef.current = requestAnimationFrame(animateSweep);
    };

    animScanRef.current = requestAnimationFrame(animateSweep);

    return () => {
      if (animScanRef.current) cancelAnimationFrame(animScanRef.current);
    };
  }, [isAutoScanning]);


  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    const handleGlobalMouseMove = (e) => {
      if (isDragging) handleMove(e.clientX);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [isDragging]);

  const handleDownloadLUT = () => {
    setIsCopiedLut(true);
    setTimeout(() => setIsCopiedLut(false), 2400);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '20px', md: '28px' },
          bgcolor: isDarkMode ? '#050a16' : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#0f172a',
          backgroundImage: 'none',
          boxShadow: isDarkMode
            ? '0 30px 80px rgba(0, 0, 0, 0.9), 0 0 50px rgba(139, 92, 246, 0.25)'
            : '0 30px 70px rgba(15, 23, 42, 0.25)',
          border: isDarkMode
            ? '1.5px solid rgba(139, 92, 246, 0.35)'
            : '1.5px solid rgba(139, 92, 246, 0.2)',
          maxHeight: '94vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3, md: 3.5 }, overflowY: 'auto' }}>
        {/* Header Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '13px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)',
              }}
            >
              <ColorLensRoundedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="h6"
                  fontWeight={900}
                  sx={{ fontSize: { xs: '1.05rem', sm: '1.3rem' }, lineHeight: 1.2 }}
                >
                  3D LUT Color Grading Sandbox
                </Typography>
                <Chip
                  label="Filmhook Editor Engine"
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.66rem',
                    bgcolor: 'rgba(139, 92, 246, 0.15)',
                    color: '#c084fc',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    height: 22,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.74rem' }}
              >
                Drag the slider to test real-time Hollywood color science over raw 4K cinema footage
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              component="a"
              href={EDITOR_PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GooglePlayIcon size={16} />}
              endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
              sx={{
                background: 'linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%)',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.8rem',
                borderRadius: '16px',
                px: 2,
                py: 0.8,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #7c3aed 0%, #db2777 100%)',
                },
              }}
            >
              Open Filmhook Editor
            </Button>

            <IconButton
              onClick={onClose}
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                '&:hover': {
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
                },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            MAIN INTERACTIVE SPLIT-SCREEN VIEWPORT
            ══════════════════════════════════════════════════════════ */}
        <Box
          ref={containerRef}
          onMouseDown={() => setIsDragging(true)}
          onTouchMove={handleTouchMove}
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 260, sm: 380, md: 440 },
            borderRadius: '24px',
            overflow: 'hidden',
            bgcolor: '#000000',
            userSelect: 'none',
            cursor: 'ew-resize',
            border: isDarkMode
              ? '2px solid rgba(139, 92, 246, 0.35)'
              : '2px solid rgba(139, 92, 246, 0.25)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            mb: 2.5,
          }}
        >
          {/* 1. Underlying Base: AFTER / 3D LUT GRADED IMAGE (Right Side) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={activeScene.image}
              alt={activeScene.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                filter: `${activeLut.filter} opacity(${intensity / 100})`,
                transition: isDragging ? 'none' : 'filter 0.3s ease',
              }}
            />

            {/* Stylized LUT Color Blend Layer */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: activeLut.overlay,
                mixBlendMode: activeLut.blendMode || 'color',
                opacity: (intensity / 100) * 0.75,
                pointerEvents: 'none',
              }}
            />

            {/* Simulated 35mm Analog Film Grain Overlay */}
            {enableGrain && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background:
                    'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%)',
                  mixBlendMode: 'overlay',
                  pointerEvents: 'none',
                  opacity: 0.8,
                }}
              />
            )}
          </Box>

          {/* 2. Top Layer: BEFORE / RAW LOG-C UNGRADED FOOTAGE (Clipped to sliderPos) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${sliderPos}%`,
              height: '100%',
              overflow: 'hidden',
              borderRight: isAutoScanning
                ? '3px solid #06b6d4'
                : '2.5px solid #ffffff',
              boxShadow: isAutoScanning
                ? '0 0 18px #06b6d4, 0 0 36px #f59e0b, 4px 0 24px rgba(0,0,0,0.7)'
                : '4px 0 24px rgba(0,0,0,0.7)',
              zIndex: 3,
            }}
          > 
            <Box
              component="img"
              src={activeScene.image}
              alt="Raw Sensor Log"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: containerRef.current ? containerRef.current.offsetWidth : '100%',
                height: '100%',
                maxWidth: 'none',
                objectFit: 'cover',
                objectPosition: 'center',
                // Log-C Flat profile simulation: low contrast, neutral saturation
                filter: 'contrast(0.78) saturate(0.7) brightness(1.08)',
              }}
            />

            {/* RAW Overlay Label */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                bgcolor: 'rgba(2, 6, 23, 0.82)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                px: 1.4,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8' }} />
              <Typography sx={{ color: '#ffffff', fontSize: '0.68rem', fontWeight: 800 }}>
                RAW LOG-C SENSOR (FLAT)
              </Typography>
            </Box>
          </Box>

          {/* 3D LUT Master Label (Right Side) */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(2, 6, 23, 0.85)',
              backdropFilter: 'blur(8px)',
              border: `1.5px solid ${activeLut.color}`,
              borderRadius: '12px',
              px: 1.4,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              zIndex: 4,
              boxShadow: `0 4px 14px ${activeLut.color}40`,
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: activeLut.color }} />
            <Typography sx={{ color: '#ffffff', fontSize: '0.68rem', fontWeight: 800 }}>
              3D LUT: {activeLut.name}
            </Typography>
          </Box>

          {/* 3. Anamorphic 2.39:1 Cinemascope Matte Bars */}
          {enableCinemascope && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: { xs: 20, sm: 30 },
                  bgcolor: '#000000',
                  zIndex: 6,
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: { xs: 20, sm: 30 },
                  bgcolor: '#000000',
                  zIndex: 6,
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          {/* Laser Beam Scanner Line Effect during Auto-Wipe */}
          {isAutoScanning && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${sliderPos}%`,
                width: '4px',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(180deg, #06b6d4 0%, #ffffff 50%, #f59e0b 100%)',
                boxShadow:
                  '0 0 14px #06b6d4, 0 0 28px #06b6d4, 0 0 40px #f59e0b, 0 0 60px #f59e0b',
                zIndex: 5,
                pointerEvents: 'none',
              }}
            >
              {/* Top Laser emitter dot */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#06b6d4',
                  boxShadow: '0 0 14px #06b6d4',
                }}
              />
              {/* Bottom Laser emitter dot */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#f59e0b',
                  boxShadow: '0 0 14px #f59e0b',
                }}
              />
            </Box>
          )}

          {/* 4. Interactive Drag Handle on Split Line */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${sliderPos}%`,
              transform: 'translate(-50%, -50%)',
              width: 38,
              height: 38,
              borderRadius: '50%',
              bgcolor: isAutoScanning ? '#020617' : '#ffffff',
              color: isAutoScanning ? '#38bdf8' : '#0f172a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 7,
              boxShadow: isAutoScanning
                ? '0 0 20px #06b6d4, 0 0 35px #f59e0b, 0 4px 18px rgba(0,0,0,0.8)'
                : '0 4px 18px rgba(0,0,0,0.8), 0 0 10px rgba(255,255,255,0.6)',
              cursor: 'ew-resize',
              border: isAutoScanning
                ? '2px solid #38bdf8'
                : `2px solid ${activeLut.color}`,
              transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
            }}
          >
            <CompareRoundedIcon sx={{ fontSize: 20 }} />

            {/* Glowing HUD tag above the handle during auto scan */}
            {isAutoScanning && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 44,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(2, 6, 23, 0.92)',
                  border: '1.5px solid #06b6d4',
                  boxShadow: '0 0 12px rgba(6, 182, 212, 0.6)',
                  borderRadius: '20px',
                  px: 1.2,
                  py: 0.3,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.6,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    boxShadow: '0 0 8px #22c55e',
                  }}
                />
                <Typography
                  sx={{
                    color: '#38bdf8',
                    fontSize: '0.62rem',
                    fontWeight: 900,
                    letterSpacing: '0.06em',
                  }}
                >
                  AUTO WIPE SCANNING
                </Typography>
              </Box>
            )}
          </Box>

          {/* Floating Auto-Play Preview Button docked in Viewport */}
          <Box
            sx={{
              position: 'absolute',
              bottom: enableCinemascope ? { xs: 26, sm: 38 } : 14,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 7,
            }}
          >
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsAutoScanning(!isAutoScanning);
              }}
              startIcon={
                isAutoScanning ? (
                  <PauseRoundedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                background: isAutoScanning
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'rgba(2, 6, 23, 0.82)',
                backdropFilter: 'blur(10px)',
                color: '#ffffff',
                fontWeight: 900,
                fontSize: '0.74rem',
                borderRadius: '20px',
                px: 2,
                py: 0.55,
                textTransform: 'none',
                boxShadow: isAutoScanning
                  ? '0 0 20px rgba(239, 68, 68, 0.7)'
                  : '0 4px 16px rgba(0,0,0,0.6)',
                border: isAutoScanning
                  ? '1.5px solid #ef4444'
                  : '1px solid rgba(255, 255, 255, 0.3)',
                transition: 'all 0.25s ease',
                '&:hover': {
                  background: isAutoScanning ? '#dc2626' : 'rgba(15, 23, 42, 0.95)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              {isAutoScanning ? 'Stop Auto-Wipe' : '🎞️ Auto-Play Preview Scanner'}
            </Button>
          </Box>

          {/* Real-Time Dynamic RGB Parade & Color Vectorscope HUD */}
          {showScopes && (
            <FilmhookColoristScopeHUD
              activeLut={activeLut}
              sliderPos={sliderPos}
              intensity={intensity}
              onClose={() => setShowScopes(false)}
            />
          )}
        </Box>

        {/* ══════════════════════════════════════════════════════════
            CINEMATIC PRESET PICKER & CONTROL TOOLBAR
            ══════════════════════════════════════════════════════════ */}
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                fontSize: '0.72rem',
                color: isDarkMode ? '#e2e8f0' : '#334155',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Select 3D LUT Presets
            </Typography>

            {/* Quick Test Scene Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.7rem' }}
              >
                Sample Footage:
              </Typography>
              {SCENE_OPTIONS.map((scene) => (
                <Chip
                  key={scene.id}
                  label={scene.title}
                  onClick={() => setSelectedSceneId(scene.id)}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    cursor: 'pointer',
                    bgcolor:
                      selectedSceneId === scene.id
                        ? 'rgba(139, 92, 246, 0.2)'
                        : isDarkMode
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f1f5f9',
                    color:
                      selectedSceneId === scene.id
                        ? '#c084fc'
                        : isDarkMode
                        ? '#94a3b8'
                        : '#64748b',
                    border:
                      selectedSceneId === scene.id
                        ? '1.5px solid #8b5cf6'
                        : '1px solid transparent',
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* LUT Cards Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
              gap: 1.2,
            }}
          >
            {LUT_PRESETS.map((lut) => {
              const isSelected = lut.id === selectedLutId;
              return (
                <Box
                  key={lut.id}
                  onClick={() => setSelectedLutId(lut.id)}
                  sx={{
                    p: 1.4,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    bgcolor: isSelected
                      ? isDarkMode
                        ? 'rgba(139, 92, 246, 0.18)'
                        : '#faf5ff'
                      : isDarkMode
                      ? 'rgba(255, 255, 255, 0.04)'
                      : '#f8fafc',
                    border: isSelected
                      ? `2px solid ${lut.color}`
                      : isDarkMode
                      ? '1px solid rgba(255, 255, 255, 0.08)'
                      : '1px solid #e2e8f0',
                    boxShadow: isSelected ? `0 6px 20px ${lut.color}35` : 'none',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.22s ease',
                    textAlign: 'left',
                    '&:hover': {
                      transform: 'scale(1.03)',
                      borderColor: lut.color,
                    },
                  }}
                >
                  {/* Swatch & Icon */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: lut.swatch,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: '0.78rem',
                          color: isSelected ? lut.color : isDarkMode ? '#ffffff' : '#0f172a',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {lut.icon} {lut.name}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    sx={{
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      fontSize: '0.66rem',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      height: 28,
                    }}
                  >
                    {lut.subtitle}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            COLOR PIPELINE SETTINGS (INTENSITY, GRAIN, CINEMASCOPE)
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            p: 1.8,
            borderRadius: '18px',
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
          }}
        >
          {/* LUT Intensity Slider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: { xs: '100%', sm: 260 } }}>
            <TuneRoundedIcon sx={{ fontSize: 18, color: activeLut.color }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800 }}>
                  LUT Opacity / Intensity
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: activeLut.color }}>
                  {intensity}%
                </Typography>
              </Box>
              <Slider
                value={intensity}
                onChange={(_, v) => setIntensity(v)}
                size="small"
                min={10}
                max={100}
                sx={{
                  color: activeLut.color,
                  height: 4,
                  p: 0,
                  '& .MuiSlider-thumb': { width: 14, height: 14 },
                }}
              />
            </Box>
          </Box>

          {/* Toggle Switches */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Toggle 35mm Analog Film Grain Texture" arrow>
              <Chip
                icon={<GrainRoundedIcon sx={{ fontSize: 16 }} />}
                label={enableGrain ? 'Film Grain: ON' : 'Film Grain: OFF'}
                onClick={() => setEnableGrain(!enableGrain)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  bgcolor: enableGrain ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
                  color: enableGrain ? '#f59e0b' : isDarkMode ? '#94a3b8' : '#64748b',
                  border: enableGrain ? '1.5px solid #f59e0b' : '1px solid #cbd5e1',
                }}
              />
            </Tooltip>

            <Tooltip title="Toggle 2.39:1 Anamorphic Cinemascope Aspect Ratio" arrow>
              <Chip
                icon={<AspectRatioRoundedIcon sx={{ fontSize: 16 }} />}
                label={enableCinemascope ? '2.39:1 Cinemascope' : '16:9 Full Screen'}
                onClick={() => setEnableCinemascope(!enableCinemascope)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  bgcolor: enableCinemascope ? 'rgba(6, 182, 212, 0.15)' : 'transparent',
                  color: enableCinemascope ? '#06b6d4' : isDarkMode ? '#94a3b8' : '#64748b',
                  border: enableCinemascope ? '1.5px solid #06b6d4' : '1px solid #cbd5e1',
                }}
              />
            </Tooltip>

            <Tooltip title="Automated Cinema Wipe Scanner (Smooth hands-free color comparison)" arrow>
              <Chip
                icon={
                  isAutoScanning ? (
                    <PauseRoundedIcon sx={{ fontSize: 16, color: '#ffffff !important' }} />
                  ) : (
                    <PlayArrowRoundedIcon sx={{ fontSize: 16, color: '#ffffff !important' }} />
                  )
                }
                label={isAutoScanning ? 'Auto-Wipe: RUNNING' : 'Auto-Wipe Scanner'}
                onClick={() => setIsAutoScanning(!isAutoScanning)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 900,
                  fontSize: '0.72rem',
                  background: isAutoScanning
                    ? 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)'
                    : 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)',
                  color: '#ffffff',
                  boxShadow: isAutoScanning
                    ? '0 0 14px rgba(239, 68, 68, 0.6)'
                    : '0 2px 10px rgba(6, 182, 212, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.04)',
                  },
                }}
              />
            </Tooltip>

            <Tooltip title="Real-Time RGB Parade, Vectorscope & Luma Waveform HUD" arrow>
              <Chip
                icon={<RadarRoundedIcon sx={{ fontSize: 16, color: showScopes ? '#38bdf8 !important' : 'inherit' }} />}
                label={showScopes ? 'Scopes HUD: ON' : 'Scopes HUD: OFF'}
                onClick={() => setShowScopes(!showScopes)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  bgcolor: showScopes ? 'rgba(56, 189, 248, 0.18)' : 'transparent',
                  color: showScopes ? '#38bdf8' : isDarkMode ? '#94a3b8' : '#64748b',
                  border: showScopes ? '1.5px solid #38bdf8' : '1px solid #cbd5e1',
                }}
              />
            </Tooltip>
          </Box>

          {/* Color Scope Specs & Download LUT */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              onClick={handleDownloadLUT}
              startIcon={
                isCopiedLut ? (
                  <CheckRoundedIcon sx={{ color: '#10b981' }} />
                ) : (
                  <DownloadRoundedIcon />
                )
              }
              sx={{
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
                color: isDarkMode ? '#ffffff' : '#0f172a',
                fontSize: '0.75rem',
                fontWeight: 800,
                borderRadius: '12px',
                py: 0.6,
                px: 1.6,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.14)' : '#cbd5e1',
                },
              }}
            >
              {isCopiedLut ? 'LUT Pack Saved!' : `Export ${activeLut.name} (.cube)`}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FilmhookColorLUTSandboxModal;
